// TemplatesPage — route: /templates
// Table of config templates. 'Apply to Tenant' opens: tenant selector →
// TemplateDiffPanel preview → Confirm.

import { useState } from "react";
import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { AdminNav } from "../components/AdminNav.js";
import { TemplateDiffPanel } from "../components/TemplateDiffPanel.js";
import {
  listTemplates, listTenants, applyTemplate,
  type ConfigTemplate, type TenantSummary, type TemplateDiff,
} from "../lib/adminApiClient.js";

const SEL = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

interface ApplyState {
  template:     ConfigTemplate;
  tenantId:     string;
  diff:         TemplateDiff | null;
  loadingDiff:  boolean;
  applying:     boolean;
}

// eslint-disable-next-line max-lines-per-function, complexity -- template CRUD page; list+editor+diff in one route
export function TemplatesPage(): JSX.Element {
  const { data: templates = [], isLoading, isError } = useQuery({
    queryKey: ["config-templates"],
    queryFn: listTemplates,
  });
  const { data: tenantList } = useQuery({
    queryKey: ["super-admin-tenants-mini"],
    queryFn:  listTenants,
  });
  const tenants: TenantSummary[] = tenantList?.data ?? [];

  const [apply, setApply] = useState<ApplyState | null>(null);

  async function openApply(tpl: ConfigTemplate): Promise<void> {
    const firstId = tenants[0]?._id ?? "";
    const state: ApplyState = { template: tpl, tenantId: firstId, diff: null, loadingDiff: false, applying: false };
    setApply(state);
    if (firstId) {
      setApply((a) => a ? { ...a, loadingDiff: true } : null);
      try {
        const diff = await applyTemplate(firstId, tpl._id, true) as TemplateDiff;
        setApply((a) => a ? { ...a, diff, loadingDiff: false } : null);
      } catch {
        setApply((a) => a ? { ...a, loadingDiff: false } : null);
      }
    }
  }

  async function changeTenant(tenantId: string): Promise<void> {
    if (!apply) return;
    setApply((a) => a ? { ...a, tenantId, diff: null, loadingDiff: true } : null);
    try {
      const diff = await applyTemplate(tenantId, apply.template._id, true) as TemplateDiff;
      setApply((a) => a ? { ...a, diff, loadingDiff: false } : null);
    } catch {
      setApply((a) => a ? { ...a, loadingDiff: false } : null);
    }
  }

  async function confirmApply(): Promise<void> {
    if (!apply) return;
    setApply((a) => a ? { ...a, applying: true } : null);
    try {
      await applyTemplate(apply.tenantId, apply.template._id, false);
      toast.success(`Template "${apply.template.template_name}" applied.`);
      setApply(null);
    } catch {
      toast.error("Failed to apply template.");
      setApply((a) => a ? { ...a, applying: false } : null);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNav />
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <h1 className="text-xl font-semibold text-slate-900">Config Templates</h1>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          {isLoading && <p className="py-10 text-center text-sm text-slate-400">Loading…</p>}
          {isError   && <p className="py-10 text-center text-sm text-red-600">Failed to load templates.</p>}
          {!isLoading && !isError && templates.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No templates found.</p>}
          {!isLoading && !isError && templates.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-3 px-4 font-medium">Name</th>
                  <th className="py-3 px-4 font-medium">Description</th>
                  <th className="py-3 px-4 font-medium">Created</th>
                  <th className="py-3 px-4" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {templates.map((t) => (
                  <tr key={t._id} className="hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-800">{t.template_name}</td>
                    <td className="py-3 px-4 text-slate-500 max-w-xs truncate">{t.description || "—"}</td>
                    <td className="py-3 px-4 text-slate-400 text-xs">
                      {formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}
                    </td>
                    <td className="py-3 px-4">
                      <button onClick={() => void openApply(t)} className="px-3 py-1 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                        Apply to Tenant
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {apply && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-900">Apply: {apply.template.template_name}</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Target Tenant</label>
              <select value={apply.tenantId} onChange={(e) => void changeTenant(e.target.value)} className={SEL}>
                {tenants.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
            </div>
            {apply.loadingDiff && <p className="text-sm text-slate-400 py-2">Loading diff…</p>}
            {apply.diff && <TemplateDiffPanel diff={apply.diff} />}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setApply(null)} className="px-3 py-1.5 text-sm rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">Cancel</button>
              <button onClick={() => void confirmApply()} disabled={!apply.diff || apply.applying || apply.loadingDiff}
                className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                {apply.applying ? "Applying…" : "Confirm Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
